(() => {
  if (customElements.get('crystal-product-selector')) return;

  const sectionCache = new Map();

  class CrystalProductSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.sectionId = this.dataset.sectionId;
      this.currentProductUrl = this.dataset.currentProductUrl;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('pointerover', this.onPointerOver.bind(this));
      requestAnimationFrame(() => {
        this.centerChoice(this.querySelector('[data-crystal-shape][aria-current="true"]'), false);
        this.centerChoice(this.querySelector('[data-crystal-variant][aria-pressed="true"]'), false);
      });
    }

    onClick(event) {
      const shapeLink = event.target.closest('[data-crystal-shape]');
      if (shapeLink && this.contains(shapeLink)) {
        event.preventDefault();
        if (shapeLink.getAttribute('aria-current') === 'true') return;
        this.loadProduct(shapeLink.dataset.productUrl, shapeLink);
        return;
      }

      const variantButton = event.target.closest('[data-crystal-variant]');
      if (variantButton && this.contains(variantButton) && !variantButton.disabled) {
        this.selectVariant(variantButton);
      }
    }

    onPointerOver(event) {
      const shapeLink = event.target.closest('[data-crystal-shape]');
      if (!shapeLink || !this.contains(shapeLink) || shapeLink.getAttribute('aria-current') === 'true') return;
      this.getSection(shapeLink.dataset.productUrl).catch(() => {});
    }

    sectionRequestUrl(productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    async getSection(productUrl) {
      const requestUrl = this.sectionRequestUrl(productUrl);
      if (!sectionCache.has(requestUrl)) {
        sectionCache.set(requestUrl, fetch(requestUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then((response) => {
          if (!response.ok) throw new Error(`Unable to load product section (${response.status})`);
          return response.text();
        }).catch((error) => {
          sectionCache.delete(requestUrl);
          throw error;
        }));
      }
      return sectionCache.get(requestUrl);
    }

    async loadProduct(productUrl, shapeLink) {
      if (!productUrl || this.getAttribute('aria-busy') === 'true') return;
      const status = this.querySelector('[data-crystal-status]');
      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = `Loading ${shapeLink.textContent.trim()}…`;

      try {
        const html = await this.getSection(productUrl);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextSection = parsed.getElementById(`MainProduct-${this.sectionId}`);
        const currentSection = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!nextSection || !currentSection) throw new Error('Product section markup was not found.');
        const nextTitle = nextSection.querySelector('[data-crystal-product-title] h1')?.textContent?.trim();

        this.replaceAll(currentSection, nextSection, '[data-crystal-product-media]');
        this.replaceOne(currentSection, nextSection, '[data-crystal-product-title]');
        this.replaceOne(currentSection, nextSection, '[data-crystal-product-price]');
        this.replaceOne(currentSection, nextSection, '[data-crystal-product-terms]');
        this.replaceOne(currentSection, nextSection, '[data-crystal-product-share]');
        this.replaceOne(currentSection, nextSection, `#Sku-${this.sectionId}`);
        this.replaceOne(currentSection, nextSection, `#Inventory-${this.sectionId}`);

        const currentModal = document.getElementById(`ProductModal-${this.sectionId}`);
        const nextModal = nextSection.querySelector(`#ProductModal-${this.sectionId}`);
        if (currentModal && nextModal) currentModal.replaceWith(nextModal);

        const nextSelector = nextSection.querySelector(`crystal-product-selector[data-section-id="${this.sectionId}"]`);
        const currentInfo = currentSection.querySelector(`#ProductInfo-${this.sectionId}`);
        if (currentInfo) currentInfo.dataset.url = productUrl;

        if (!nextSelector) throw new Error('Shape selector markup was not found.');
        this.replaceWith(nextSelector);

        if (nextTitle) document.title = nextTitle;
        if (this.dataset.updateUrl !== 'false') history.replaceState({ crystalProduct: true }, '', productUrl);
        document.dispatchEvent(new CustomEvent('crystal:product-change', { detail: { url: productUrl } }));
      } catch (error) {
        this.removeAttribute('aria-busy');
        if (status) status.textContent = 'This shape could not be loaded. Please try again.';
        console.error('[Crystal product selector]', error);
      }
    }

    replaceOne(currentSection, nextSection, selector) {
      const currentNode = currentSection.querySelector(selector);
      const nextNode = nextSection.querySelector(selector);
      if (currentNode && nextNode) currentNode.replaceWith(nextNode);
    }

    replaceAll(currentSection, nextSection, selector) {
      const currentNodes = [...currentSection.querySelectorAll(selector)];
      const nextNodes = [...nextSection.querySelectorAll(selector)];
      currentNodes.forEach((node, index) => {
        if (nextNodes[index]) node.replaceWith(nextNodes[index]);
      });
    }

    selectVariant(button) {
      this.querySelectorAll('[data-crystal-variant]').forEach((option) => {
        const selected = option === button;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', String(selected));
      });

      const continueLink = this.querySelector('[data-crystal-continue]');
      if (continueLink) continueLink.href = button.dataset.personalizerUrl;
      this.centerChoice(button, true);
      this.updatePrice(button);
      this.updateMedia(button);
      this.updateVariantInputs(button.dataset.variantId);
      if (this.dataset.updateUrl !== 'false') history.replaceState({ crystalVariant: true }, '', button.dataset.variantUrl);
      const optionLabel = button.querySelector(':scope > strong')?.textContent?.trim() || 'Option';
      this.querySelector('[data-crystal-status]').textContent = `${optionLabel} selected.`;
      document.dispatchEvent(new CustomEvent('crystal:variant-change', { detail: { id: button.dataset.variantId } }));
    }

    centerChoice(choice, smooth) {
      const scroller = choice?.parentElement;
      if (!choice || !scroller) return;
      const targetLeft = choice.offsetLeft - (scroller.clientWidth - choice.clientWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, targetLeft), behavior: smooth ? 'smooth' : 'auto' });
    }

    updatePrice(button) {
      const section = document.getElementById(`MainProduct-${this.sectionId}`);
      const price = section?.querySelector('[data-crystal-product-price] .price');
      if (!price) return;
      const onSale = Number(button.dataset.compareCents || 0) > Number(button.dataset.priceCents || 0);
      price.classList.toggle('price--on-sale', onSale);
      price.classList.toggle('price--sold-out', button.dataset.available !== 'true');
      const regular = price.querySelector('.price__regular .price-item--regular');
      const saleCompare = price.querySelector('.price__sale s.price-item--regular');
      const saleCurrent = price.querySelector('.price__sale .price-item--sale');
      if (regular) regular.textContent = button.dataset.price;
      if (saleCompare) saleCompare.textContent = button.dataset.comparePrice || '';
      if (saleCurrent) saleCurrent.textContent = button.dataset.price;
    }

    updateMedia(button) {
      const mediaId = button.dataset.mediaId;
      const imageUrl = button.dataset.imageUrl;
      const section = document.getElementById(`MainProduct-${this.sectionId}`);
      section?.querySelectorAll('[data-crystal-product-media] media-gallery').forEach((gallery) => {
        if (mediaId && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.sectionId}-${mediaId}`, true);
          return;
        }
        if (!imageUrl) return;
        const activeImage = gallery.querySelector('.product__media-item.is-active img') || gallery.querySelector('.product__media-item img');
        if (activeImage) {
          activeImage.removeAttribute('srcset');
          activeImage.removeAttribute('sizes');
          activeImage.src = imageUrl;
          activeImage.alt = button.dataset.imageAlt || '';
        }
      });
    }

    updateVariantInputs(variantId) {
      if (!variantId) return;
      const section = document.getElementById(`MainProduct-${this.sectionId}`);
      section?.querySelectorAll('form.installment input[name="id"]').forEach((input) => { input.value = variantId; });
    }
  }

  customElements.define('crystal-product-selector', CrystalProductSelector);
})();
